import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAppleSignInAvailable } from '../helpers/appleSignIn'
import { isGoogleSignInAvailable } from '../helpers/googleSignIn'
import ButtonSpinner from './ButtonSpinner'

type SocialAuthButtonsProps = {
  busyProvider: 'apple' | 'google' | null
  onPressApple: () => void
  onPressGoogle: () => void
  showDividerBelow?: boolean
}

/**
 * Apple + Google sign-in buttons for funnel auth screens. Hidden when the
 * corresponding Vite env vars are unset, so other funnels stay unchanged.
 */
export default function SocialAuthButtons({
  busyProvider,
  onPressApple,
  onPressGoogle,
  showDividerBelow = false,
}: SocialAuthButtonsProps) {
  const { t } = useTranslation()
  const [appleAvailable, setAppleAvailable] = useState(false)
  const [googleAvailable, setGoogleAvailable] = useState(false)
  const [availabilityResolved, setAvailabilityResolved] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Resolved separately: the Google check waits for the Google Identity
    // Services script, which must not hold back the Apple button.
    void isAppleSignInAvailable().then((available) => {
      if (cancelled) return
      setAppleAvailable(available)
      setAvailabilityResolved(true)
    })
    void isGoogleSignInAvailable().then((available) => {
      if (cancelled) return
      setGoogleAvailable(available)
      setAvailabilityResolved(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!availabilityResolved || (!appleAvailable && !googleAvailable)) {
    return null
  }

  const disabled = busyProvider !== null
  const isApplePending = busyProvider === 'apple'
  const isGooglePending = busyProvider === 'google'

  return (
    <>
      <div className="w-full space-y-3">
        {appleAvailable ? (
          <SocialProviderButton
            busy={isApplePending}
            className="bg-black text-white"
            disabled={disabled}
            label={t('social.continueWithApple')}
            onClick={onPressApple}
            variant="apple"
          />
        ) : null}
        {googleAvailable ? (
          <SocialProviderButton
            busy={isGooglePending}
            className="border border-[#dadce0] bg-white text-[#3c4043]"
            disabled={disabled}
            label={t('social.continueWithGoogle')}
            onClick={onPressGoogle}
            variant="google"
          />
        ) : null}
      </div>
      {showDividerBelow ? <SocialOrDivider /> : null}
    </>
  )
}

function SocialOrDivider() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3">
      <div className="h-px grow bg-[#e5e5e5]" />
      <span className="text-sm text-[#a1a1a1]">{t('social.orDivider')}</span>
      <div className="h-px grow bg-[#e5e5e5]" />
    </div>
  )
}

function SocialProviderButton({
  busy,
  className,
  disabled,
  label,
  onClick,
  variant,
}: {
  busy: boolean
  className: string
  disabled: boolean
  label: string
  onClick: () => void
  variant: 'apple' | 'google'
}) {
  return (
    <button
      aria-busy={busy}
      aria-label={label}
      className={`${className} font-headline inline-flex h-12 w-full items-center justify-center gap-3 rounded-xl text-base font-semibold disabled:opacity-60`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {busy ? (
        <ButtonSpinner
          className={variant === 'google' ? 'text-[#3c4043]' : undefined}
        />
      ) : (
        <>
          {variant === 'apple' ? <AppleLogo /> : <GoogleLogo />}
          <span>{label}</span>
        </>
      )}
    </button>
  )
}

function AppleLogo() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-4 shrink-0 overflow-visible fill-current"
      viewBox="4.2 2.3 15.4 19.1"
    >
      <path d="M16.365 12.708c.012-2.058 1.682-3.045 1.758-3.092-.957-1.398-2.447-1.589-2.978-1.611-1.27-.128-2.476.748-3.118.748-.642 0-1.638-.729-2.694-.708-1.388.02-2.665.806-3.381 2.045-1.44 2.498-.368 6.196 1.036 8.222.687.99 1.507 2.105 2.582 2.065 1.036-.04 1.43-.67 2.682-.67 1.253 0 1.605.67 2.703.649 1.115-.02 1.823-1.013 2.508-2.006.79-1.151 1.116-2.27 1.136-2.328-.026-.012-2.18-.836-2.234-3.314zM14.36 5.93c.572-.694.957-1.66.852-2.621-.823.034-1.821.55-2.413 1.243-.529.611-.993 1.595-.869 2.534.92.071 1.857-.467 2.43-1.156z" />
    </svg>
  )
}

function GoogleLogo() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}
